import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Plus, 
  Minus, 
  Calculator, 
  Download, 
  Send,
  Trash2,
  Copy
} from 'lucide-react';
import { adminProductsAPI } from '../../services/api';

export const QuoteGeneratorPage = () => {
  const [clientInfo, setClientInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    projectType: 'Installation résidentielle'
  });
  
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [totalHT, setTotalHT] = useState(0);
  const [tvaRate, setTvaRate] = useState(20);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  // Charger les produits réels de la base de données
  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const data = await adminProductsAPI.getAll();
      // Filtrer uniquement les produits disponibles
      const availableProducts = data.filter(p => p.availability);
      setProducts(availableProducts);
    } catch (err) {
      console.error('Erreur chargement produits:', err);
    }
  };

  const addProduct = (product) => {
    const existing = selectedProducts.find(p => p.id === product.id);
    if (existing) {
      setSelectedProducts(prev => 
        prev.map(p => p.id === product.id ? {...p, quantity: p.quantity + 1} : p)
      );
    } else {
      setSelectedProducts(prev => [...prev, {...product, quantity: 1}]);
    }
  };

  const removeProduct = (productId) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeProduct(productId);
      return;
    }
    setSelectedProducts(prev => 
      prev.map(p => p.id === productId ? {...p, quantity} : p)
    );
  };

  const updatePrice = (productId, price) => {
    setSelectedProducts(prev => 
      prev.map(p => p.id === productId ? {...p, price: parseFloat(price) || 0} : p)
    );
  };

  useEffect(() => {
    const subtotal = selectedProducts.reduce((sum, product) => 
      sum + (product.price * product.quantity), 0
    );
    const discountAmount = (subtotal * discountPercent) / 100;
    setTotalHT(subtotal - discountAmount);
  }, [selectedProducts, discountPercent]);

  const totalTTC = totalHT * (1 + tvaRate / 100);
  const tvaAmount = totalTTC - totalHT;

  const generatePDF = () => {
    // Ici on pourrait intégrer une librairie PDF comme jsPDF
    alert('Fonctionnalité PDF à venir. Pour l\'instant, utilisez "Imprimer" depuis votre navigateur.');
    window.print();
  };

  const sendQuote = async () => {
    try {
      setLoading(true);
      // Ici on enverrait le devis par email
      alert(`Devis envoyé à ${clientInfo.email}`);
    } catch (err) {
      alert('Erreur lors de l\'envoi du devis');
    } finally {
      setLoading(false);
    }
  };

  const copyQuoteText = () => {
    const quoteText = `
DEVIS Z&N SOLAR - L'excellence sans compromis

Client: ${clientInfo.name}
Email: ${clientInfo.email}
Téléphone: ${clientInfo.phone}
Adresse: ${clientInfo.address}
Type de projet: ${clientInfo.projectType}

PRODUITS:
${selectedProducts.map(p => 
  `- ${p.name} x${p.quantity} = ${(p.price * p.quantity).toFixed(2)}€ HT`
).join('\n')}

RÉCAPITULATIF:
Sous-total HT: ${selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0).toFixed(2)}€
${discountPercent > 0 ? `Remise (${discountPercent}%): -${((selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0) * discountPercent) / 100).toFixed(2)}€` : ''}
Total HT: ${totalHT.toFixed(2)}€
TVA (${tvaRate}%): ${tvaAmount.toFixed(2)}€
TOTAL TTC: ${totalTTC.toFixed(2)}€

Notes: ${notes}

Garantie: 10 ans pièces et main d'œuvre
Conformité CE - Plus de 6000 cycles

Z&N Solar - Distributeur Exclusif PowMr
📞 06 23 73 77 80 | ✉️ contact@znsolar.fr
    `;
    
    navigator.clipboard.writeText(quoteText);
    alert('Devis copié dans le presse-papiers !');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Générateur de Devis</h2>
        <p className="text-gray-600 mt-1">Créez des devis personnalisés pour vos clients</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Client Information */}
        <Card>
          <CardHeader>
            <CardTitle>Informations Client</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              placeholder="Nom du client"
              value={clientInfo.name}
              onChange={(e) => setClientInfo(prev => ({...prev, name: e.target.value}))}
            />
            <Input
              placeholder="Email"
              type="email"
              value={clientInfo.email}
              onChange={(e) => setClientInfo(prev => ({...prev, email: e.target.value}))}
            />
            <Input
              placeholder="Téléphone"
              value={clientInfo.phone}
              onChange={(e) => setClientInfo(prev => ({...prev, phone: e.target.value}))}
            />
            <Input
              placeholder="Adresse"
              value={clientInfo.address}
              onChange={(e) => setClientInfo(prev => ({...prev, address: e.target.value}))}
            />
            <select 
              className="w-full border rounded px-3 py-2"
              value={clientInfo.projectType}
              onChange={(e) => setClientInfo(prev => ({...prev, projectType: e.target.value}))}
            >
              <option value="Installation résidentielle">Installation résidentielle</option>
              <option value="Installation professionnelle">Installation professionnelle</option>
              <option value="Installation industrielle">Installation industrielle</option>
              <option value="Maintenance">Maintenance</option>
            </select>
          </CardContent>
        </Card>

        {/* Product Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Sélection des Produits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {products.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">Chargement des produits...</p>
              ) : (
                products.map(product => {
                  // Extraire le prix numérique
                  const price = product.price === 'Sur devis' ? 0 : parseFloat(product.price) || 0;
                  
                  return (
                    <div key={product.id} className="flex items-center justify-between p-2 border rounded hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{product.name}</div>
                        <div className="text-xs text-gray-500">
                          {product.price === 'Sur devis' ? 'Sur devis' : `${price}€ HT`}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => addProduct({...product, price})}
                        className="bg-yellow-600 hover:bg-yellow-700"
                        disabled={product.price === 'Sur devis'}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Selected Products */}
      {selectedProducts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Produits Sélectionnés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {selectedProducts.map(product => (
                <div key={product.id} className="flex items-center space-x-4 p-3 border rounded">
                  <div className="flex-1">
                    <div className="font-medium">{product.name}</div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateQuantity(product.id, product.quantity - 1)}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    
                    <Input
                      type="number"
                      min="1"
                      value={product.quantity}
                      onChange={(e) => updateQuantity(product.id, parseInt(e.target.value) || 1)}
                      className="w-16 text-center"
                    />
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateQuantity(product.id, product.quantity + 1)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  <Input
                    type="number"
                    step="0.01"
                    value={product.price}
                    onChange={(e) => updatePrice(product.id, e.target.value)}
                    className="w-24"
                    placeholder="Prix HT"
                  />

                  <div className="w-20 text-right font-medium">
                    {(product.price * product.quantity).toFixed(2)}€
                  </div>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeProduct(product.id)}
                    className="text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quote Summary */}
      {selectedProducts.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Paramètres du Devis</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Remise (%)</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(parseFloat(e.target.value) || 0)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">TVA (%)</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={tvaRate}
                  onChange={(e) => setTvaRate(parseFloat(e.target.value) || 20)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  className="w-full border rounded px-3 py-2 h-20"
                  placeholder="Notes additionnelles pour le devis..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calculator className="w-5 h-5" />
                <span>Récapitulatif</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Sous-total HT:</span>
                  <span>{selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0).toFixed(2)}€</span>
                </div>
                
                {discountPercent > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Remise ({discountPercent}%):</span>
                    <span>-{((selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0) * discountPercent) / 100).toFixed(2)}€</span>
                  </div>
                )}
                
                <div className="flex justify-between font-medium">
                  <span>Total HT:</span>
                  <span>{totalHT.toFixed(2)}€</span>
                </div>
                
                <div className="flex justify-between">
                  <span>TVA ({tvaRate}%):</span>
                  <span>{tvaAmount.toFixed(2)}€</span>
                </div>
                
                <hr />
                
                <div className="flex justify-between text-lg font-bold text-yellow-600">
                  <span>Total TTC:</span>
                  <span>{totalTTC.toFixed(2)}€</span>
                </div>
              </div>

              <div className="mt-6 space-y-2">
                <Button onClick={copyQuoteText} className="w-full" variant="outline">
                  <Copy className="w-4 h-4 mr-2" />
                  Copier le devis
                </Button>
                
                <Button onClick={generatePDF} className="w-full" variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Générer PDF
                </Button>
                
                <Button 
                  onClick={sendQuote} 
                  className="w-full bg-yellow-600 hover:bg-yellow-700"
                  disabled={!clientInfo.email || loading}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? 'Envoi...' : 'Envoyer par email'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default QuoteGeneratorPage;